import { useEffect } from 'react';
import type { GameId, RobotLevel } from '@strategy4kids/protocol';
import { Icon, InstallButton, type IconName } from '@strategy4kids/ui';
import { GameBackdrop } from '../components/GameBackdrop.js';
import { GameCover } from '../components/GameCard.js';
import { RobotPicker } from '../components/RobotPicker.js';
import { PersonaPicker } from '../components/PersonaPicker.js';
import { ThemePicker } from '../components/ThemePicker.js';
import { useT } from '../i18n/useT.js';
import { useAudioSettingsStore } from '../state/audio-settings-store.js';
import { setAmbientMood } from '../services/audio.js';
import { pirateTournamentSummary } from '../state/pirate-store.js';
import { LOCALE_ENABLED } from '../config.js';
import { LocaleSwitcher } from '../components/LocaleSwitcher.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { edition } from '../edition/index.js';
import { editionCatalog, gameEntry } from '../lib/games-catalog.js';
import { useRemoteStages } from '../lib/remote-stage-store.js';

interface SecondaryLink {
  readonly testId:
    | 'open-lobby'
    | 'open-online'
    | 'open-learn'
    | 'open-leaderboard'
    | 'open-parent'
    | 'open-settings';
  readonly label: string;
  readonly icon: IconName;
  readonly iconClass: string;
  readonly onClick: () => void;
}

/**
 * Renders one secondary-link button. The `data-testid` attribute is a string
 * literal — not a JSX expression — so the testid-audit regex (which only
 * matches quoted string literals) picks it up. Switching on the literal
 * union keeps the testid explicit per call site.
 */
function SecondaryLinkButton({ link }: { link: SecondaryLink }) {
  const buttonClass =
    'group flex min-h-[48px] min-w-0 items-center gap-2 rounded-xl bg-surface/90 px-2.5 py-1.5 text-left text-[15px] font-bold text-ink ring-1 sm:text-base ring-border transition-colors hover:bg-sun/20 xl:flex-1 xl:justify-center xl:px-3';
  const iconWrapClass = `flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-raised shadow-sm ring-1 ring-sun/30 ${link.iconClass}`;
  const inner = (
    <>
      <span className={iconWrapClass}>
        <Icon name={link.icon} size="sm" className="" />
      </span>
      <span className="min-w-0 break-words leading-tight">{link.label}</span>
    </>
  );
  switch (link.testId) {
    case 'open-lobby':
      return (
        <button type="button" data-testid="open-lobby" data-appdom-id="open-lobby" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
    case 'open-online':
      return (
        <button type="button" data-testid="open-online" data-appdom-id="open-online" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
    case 'open-learn':
      return (
        <button type="button" data-testid="open-learn" data-appdom-id="open-learn" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
    case 'open-leaderboard':
      return (
        <button type="button" data-testid="open-leaderboard" data-appdom-id="open-leaderboard" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
    case 'open-parent':
      return (
        <button type="button" data-testid="open-parent" data-appdom-id="open-parent" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
    case 'open-settings':
      return (
        <button type="button" data-testid="open-settings" data-appdom-id="open-settings" data-appdom-type="Button" onClick={link.onClick} className={buttonClass}>
          {inner}
        </button>
      );
  }
}


/** The one-time music offer: one small banner, answered once, never asked again (ratified default). */
function MusicOffer() {
  const { t } = useT('navigation');
  const { musicOfferAnswered, answerMusicOffer } = useAudioSettingsStore();
  if (musicOfferAnswered) return null;
  return (
    <div className="glass glass-light mx-auto mt-3 flex w-full flex-wrap items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm" data-testid="music-offer">
      <span>{t('music-offer')}</span>
      <button className="inline-flex min-h-[48px] items-center rounded-xl bg-accent px-4 py-2 font-semibold text-accent-fg" data-testid="music-offer-yes" onClick={() => answerMusicOffer(true)}>
        {t('music-offer-yes')}
      </button>
      <button className="inline-flex min-h-[48px] items-center rounded-xl px-4 py-2 font-semibold text-fg underline-offset-2 hover:underline" data-testid="music-offer-no" onClick={() => answerMusicOffer(false)}>
        {t('music-offer-no')}
      </button>
    </div>
  );
}

export function Home({
  gameId,
  onOpenGames,
  onSelectGame,
  onStart,
  selected,
  onSelectLevel,
  onOpenLearn,
  onOpenOnline,
  onOpenLobby,
  onOpenLeaderboard,
  onOpenParent,
  onOpenSettings,
}: {
  gameId: GameId;
  /** Open the Games page (browse details and online badges). */
  onOpenGames: () => void;
  /** Pick the game right here on Home; no navigation. */
  onSelectGame: (id: GameId) => void;
  onStart: (level: RobotLevel) => void;
  selected: RobotLevel;
  onSelectLevel: (level: RobotLevel) => void;
  onOpenLearn: () => void;
  onOpenOnline: () => void;
  onOpenLobby: () => void;
  onOpenLeaderboard: () => void;
  onOpenParent: () => void;
  /** Open the sound settings (and the grown-up locks behind them). */
  onOpenSettings: () => void;
}) {
  const { t } = useT('navigation');
  const { t: tCommon } = useT('common');

  // Back on Home, the ambience returns to the menu mood (a no-op while music is off).
  useEffect(() => setAmbientMood('menu'), []);

  // A weekend tournament on this device keeps its own entry: standings and a way back in.
  const tournamentSummary = pirateTournamentSummary();

  const secondaryLinks: readonly SecondaryLink[] = [
    {
      testId: 'open-lobby',
      label: t('pass-and-play'),
      icon: 'Users',
      iconClass: 'text-grape',
      onClick: onOpenLobby,
    },
    {
      testId: 'open-online',
      label: t('play-online'),
      icon: 'Wifi',
      iconClass: 'text-sky',
      onClick: onOpenOnline,
    },
    {
      testId: 'open-learn',
      label: t('learn-the-game'),
      icon: 'GraduationCap',
      iconClass: 'text-grape',
      onClick: onOpenLearn,
    },
    {
      testId: 'open-leaderboard',
      label: t('leaderboard'),
      icon: 'Trophy',
      iconClass: 'text-sun',
      onClick: onOpenLeaderboard,
    },
    {
      testId: 'open-parent',
      label: t('for-parents'),
      icon: 'Users',
      iconClass: 'text-mint',
      onClick: onOpenParent,
    },
    {
      testId: 'open-settings',
      label: t('sound-and-settings'),
      icon: 'Volume2',
      iconClass: 'text-sky',
      onClick: onOpenSettings,
    },
  ];

  return (
    <ScreenLayout width="wide" className="items-stretch" backdrop={<GameBackdrop game={gameEntry(gameId)} />} data-appdom-scope="home">
      <MusicOffer />
      {tournamentSummary && (
        <button
          type="button"
          onClick={onOpenGames}
          className="min-h-[48px] glass glass-light mx-auto mt-3 flex w-full flex-wrap items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm"
          data-testid="home-tournament-continue"
        >
          <span className="font-bold">{t('pirate-tournament-title')}</span>
          <span className="text-fg-muted">
            {t('pirate-tournament-session')} {tournamentSummary.sessionWins} ({tournamentSummary.games}) · {t('pirate-tournament-series')} {tournamentSummary.seriesWins}
          </span>
          <span className="font-semibold text-accent underline">{t('pirate-tournament-continue')}</span>
        </button>
      )}
      {/* Hero: the generated panorama with the brand over it. The brand mark keeps its pinned testid inside the home scope. */}
      <header className="relative isolate overflow-hidden rounded-3xl shadow-lg ring-1 ring-border" data-testid="home-hero">
        <img src="/art/hero-home.webp" alt="" aria-hidden="true" className="absolute inset-0 -z-10 size-full object-cover" />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-gradient-to-r from-bg/90 via-bg/75 to-bg/40 sm:from-bg/85 sm:via-bg/55 sm:to-transparent" />
        <div className="flex flex-row items-center gap-4 px-4 py-3 sm:gap-5 sm:px-8 sm:py-7 lg:min-h-[13rem] lg:max-w-[58%] lg:justify-start lg:gap-6 lg:px-12 lg:py-8">
          <div
            className="flex size-14 shrink-0 items-center justify-center rounded-3xl bg-surface/90 shadow-lg ring-1 ring-accent/20 sm:size-16 lg:size-20"
            data-testid="home-brand-mark"
          >
            <img src={edition.brand.logo} alt="" width={64} height={64} className="size-12 sm:size-12 lg:size-14" />
          </div>
          <div className="min-w-0 sm:max-w-[46%] lg:max-w-none">
            <h1 className="font-display text-hero font-bold tracking-tight text-fg">{edition.brand.name}</h1>
            <p className="mt-1 hidden max-w-xl text-base font-semibold text-fg-muted sm:block lg:text-lg">{edition.brand.tagline}</p>
          </div>
        </div>
      </header>

      {/* The menu: one compact strip under the hero (two columns on phones, three on tablets, a single row on desktop). */}
      <nav aria-label="Menu" className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:flex xl:flex-row xl:gap-2" data-testid="home-menu">
        {secondaryLinks.map((link) => (
          <SecondaryLinkButton key={link.testId} link={link} />
        ))}
      </nav>

      <section className="flex min-w-0 flex-col gap-4" data-testid="home-pickers">
        <div className="grid min-w-0 gap-4 lg:grid-cols-2 lg:gap-6">
          <GamePicker gameId={gameId} onSelectGame={onSelectGame} onOpenGames={onOpenGames} />
          <div className="min-w-0 sm:rounded-2xl sm:border sm:border-border sm:bg-surface-raised/95 sm:p-3 lg:p-4">
            <RobotPicker selected={selected} onSelect={onSelectLevel} onStart={() => onStart(selected)} />
          </div>
        </div>
        <PersonaPicker />
      </section>

      <footer className="flex items-center justify-center pt-1">
        <InstallButton className="" labelKey={tCommon('install')} />
      </footer>

      <details
        className="rounded-2xl border border-border bg-surface shadow-sm"
        data-testid="theme-picker"
      >
        <summary className="flex min-h-[48px] cursor-pointer items-center list-none select-none p-4 text-sm font-extrabold uppercase tracking-wide text-fg-muted [&::-webkit-details-marker]:hidden">
          Board look
        </summary>
        <div className="px-4 pb-4">
          <ThemePicker />
        </div>
      </details>

      {/* Spec §4.4: settings row behind a <details> so Home stays uncluttered.
          LocaleSwitcher renders null unless LOCALE_ENABLED, so this collapses
          to an empty disclosure rather than an empty box when the flag is off. */}
      {LOCALE_ENABLED && (
        <details
          className="rounded-2xl border border-border bg-surface shadow-sm"
          data-testid="locale-picker"
        >
          <summary className="flex min-h-[48px] cursor-pointer items-center list-none select-none p-4 text-sm font-extrabold uppercase tracking-wide text-fg-muted [&::-webkit-details-marker]:hidden">
            {tCommon('language')}
          </summary>
          <div className="px-4 pb-4">
            <LocaleSwitcher />
          </div>
        </details>
      )}
    </ScreenLayout>
  );
}

/**
 * The game choice, inline: a tile per game this edition offers. A tap selects at once (no navigation) and the robot picker
 * below follows. Wide screens get a grid (up to six a row); phones a horizontally scrolling snap row. `game-picker` and
 * `current-game-name` keep their testids for the flows; "All games" opens the Games page for the details and online badges.
 */
function GamePicker({ gameId, onSelectGame, onOpenGames }: { gameId: GameId; onSelectGame: (id: GameId) => void; onOpenGames: () => void }) {
  const { t } = useT('games');
  // The operator's remote stages can arrive after the first render, and `editionCatalog()` reads them.
  useRemoteStages();
  const current = gameEntry(gameId);
  const games = editionCatalog();
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-border bg-surface-raised/95 p-3 lg:p-4" data-testid="game-picker">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h2 className="font-display text-xl font-bold text-fg lg:text-2xl">
          {t('games-your-game')}: <span data-testid="current-game-name">{current.label}</span>
        </h2>
        <button
          type="button"
          onClick={onOpenGames}
          data-testid="open-games"
          data-appdom-id="open-games"
          data-appdom-type="Button"
          className="inline-flex min-h-[48px] items-center gap-1 rounded-lg px-3 py-2 text-base font-bold text-fg underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:text-base"
        >
          {t('games-all')}
          <Icon name="ChevronRight" size="sm" />
        </button>
      </div>
      <div
        role="group"
        aria-label={t('games-your-game')}
        data-testid="home-game-tiles"
        className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 pt-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0 md:grid-cols-6"
      >
        {games.map((g) => {
          const selected = g.id === gameId;
          return (
            <button
              key={g.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectGame(g.id)}
              data-testid={`home-game-${g.id}`}
              data-appdom-id={`home-game-${g.id}`}
              data-appdom-type="Button"
              className={`group flex w-[6.5rem] shrink-0 snap-start flex-col items-stretch gap-1.5 rounded-2xl p-1.5 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-auto ${
                selected ? 'bg-accent/15 ring-4 ring-accent' : 'ring-1 ring-border hover:bg-sun/20'
              }`}
            >
              <GameCover game={g} className="aspect-square w-full rounded-xl" />
              <span className={`truncate px-0.5 text-sm font-bold ${selected ? 'text-fg' : 'text-fg-muted'}`}>{g.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
